import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Phone, ShieldCheck, IndianRupee, Gauge, Leaf } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import Marquee from "../components/Marquee";
import { TreadPattern } from "../components/TreadPattern";
import { IMAGES, MANIFESTO, CATEGORIES, CONTACT, USPS } from "../data/site";
import { TREADS } from "../data/products";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          src={IMAGES.hero}
          alt="Heavy duty tyre tread close-up"
          className="h-full w-full object-cover opacity-40"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/75" />
        <div className="absolute inset-0 blueprint-grid" />
        <div className="absolute -top-32 right-[8%] h-[460px] w-[460px] rounded-full bg-carddark/60 blur-[130px]" />
        <div className="absolute bottom-[-140px] left-[-80px] h-[400px] w-[400px] rounded-full bg-signal/20 blur-[130px]" />
      </motion.div>

      <p className="absolute top-1/2 right-6 hidden lg:block -rotate-90 origin-right font-mono text-[10px] uppercase tracking-[0.5em] text-slate-500 whitespace-nowrap">
        Cold Process · Radial & Bias
      </p>

      <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-[1440px] px-4 sm:px-8 lg:px-16 pt-32 pb-14 sm:pb-20">
        <Eyebrow className="mb-6">{CONTACT.company} — Tripura, India</Eyebrow>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="h-[3px] w-24 bg-signal origin-left mb-8"
        />
        <MaskedLines
          delay={0.25}
          lines={[
            "HIGH PERFORMANCE",
            { node: <span className="text-outline">PRECURED TREAD</span> },
            { node: <span className="text-signal">RUBBER.</span> },
          ]}
          className="font-display font-black uppercase leading-[0.88] tracking-tight text-[clamp(3.4rem,10.5vw,10rem)]"
        />
        <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <Reveal delay={0.9} y={20} className="max-w-md">
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Precured tread rubber, bonding gum and vulcanising solution — manufactured in Tripura under the
              technical guidance of the Rubber Board, Govt. of India.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-request-quote-button"
                className="group inline-flex items-center gap-3 bg-signal px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white hover:bg-signalhover transition-colors"
              >
                Request a Quote
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/products"
                data-testid="hero-view-products-button"
                className="inline-flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white hover:border-amber hover:text-amber transition-colors"
              >
                View Products
              </Link>
            </div>
          </Reveal>
          <Reveal delay={1.05} y={20} className="flex gap-10 sm:gap-14 lg:pb-2">
            {[
              [`${TREADS.length}`, "MAX tread patterns"],
              ["04", "Product lines"],
              ["R&B", "Radial & bias types"],
            ].map(([num, label]) => (
              <div key={label}>
                <p className="font-display text-4xl sm:text-5xl font-black text-white">{num}</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{label}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </motion.div>
    </section>
  );
};

const Manifesto = () => (
  <section className="relative py-20 lg:py-28" data-testid="manifesto-section">
    <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 grid gap-14 lg:grid-cols-[1.2fr_1fr]">
      <div>
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">The Maximus Standard</p>
          <h2 className="mt-4 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white leading-[0.95]">
            Built for people who count kilometres
          </h2>
        </Reveal>
        <div className="mt-12 border-t border-white/10">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.no} delay={i * 0.06} className="border-b border-white/10">
              <div className="group flex gap-6 sm:gap-10 py-8 items-start hover:bg-white/[0.04] transition-colors" data-testid={`manifesto-chapter-${m.no}`}>
                <span className="font-display text-4xl sm:text-5xl font-black text-outline-amber shrink-0 w-16 sm:w-20">
                  {m.no}
                </span>
                <div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">
                    {m.title}
                  </h3>
                  <p className="mt-2 max-w-lg text-sm sm:text-base text-slate-400 leading-relaxed">{m.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="relative">
        <div className="lg:sticky lg:top-28">
          <Reveal delay={0.2}>
            <div className="relative clip-corner overflow-hidden" data-testid="manifesto-image-frame">
              <img src={IMAGES.factory2} alt="Rubber manufacturing machinery at the Maximus plant" className="h-72 sm:h-96 lg:h-[560px] w-full object-cover" data-testid="manifesto-factory-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">Tripura Plant</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
                  Kneader · Extruder · Calendaring · Mixing Mill · Curing Press
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Categories = () => (
  <section className="py-20 lg:py-28" data-testid="categories-section">
    <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
      <Reveal className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 mb-12">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">Product Lines</p>
          <h2 className="mt-4 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white leading-[0.95]">
            Four lines. One system.
          </h2>
        </div>
        <Link to="/products" data-testid="categories-view-all" className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 hover:text-amber transition-colors inline-flex items-center gap-2">
          Full catalogue <ArrowRight size={14} />
        </Link>
      </Reveal>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.08} className="h-full">
            <Link
              to={`/products?category=${c.id}`}
              data-testid={`category-card-${c.id}`}
              className="group flex h-full flex-col border border-white/10 bg-panel/50 p-6 sm:p-8 hover:bg-amber hover:border-amber transition-colors duration-300"
            >
              <span className="font-mono text-[10px] tracking-[0.3em] text-amber group-hover:text-ink">0{i + 1}</span>
              <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-white group-hover:text-ink transition-colors">{c.label}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed group-hover:text-ink/75 transition-colors">{c.desc}</p>
              <span className="mt-auto pt-8 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 group-hover:text-ink/60">{c.count}</span>
                <ArrowUpRight size={18} className="text-slate-500 group-hover:text-ink group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Showcase = () => {
  const featured = TREADS.slice(0, 8);
  return (
    <section className="py-20 lg:py-28 bg-deep border-y border-white/10" data-testid="showcase-section">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
        <Reveal className="mb-12">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">Tread Pattern Showcase</p>
          <h2 className="mt-4 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white leading-[0.95]">
            The MAX range
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {featured.map((t, i) => (
            <motion.div
              key={t.slug}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.07 }}
              className="group relative bg-ink p-6 hover:bg-[#16337F] transition-colors"
              data-testid={`showcase-tile-${t.slug}`}
            >
              <TreadPattern type={t.pattern} className="h-28 w-full opacity-75 group-hover:opacity-100 transition-opacity" />
              <div className="mt-5 flex items-center justify-between">
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{t.code}</h3>
                <span className="font-mono text-[9px] tracking-[0.2em] text-slate-500">{t.variants.length} SIZE{t.variants.length > 1 ? "S" : ""}</span>
              </div>
            </motion.div>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <Link
            to="/products"
            data-testid="showcase-view-range-button"
            className="group inline-flex items-center gap-3 border border-white/25 px-10 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white hover:border-amber hover:text-amber transition-colors"
          >
            Explore all {TREADS.length} patterns
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const ColdProcess = () => (
  <section className="bg-[#F8FAFC] text-deep blueprint-grid-light" data-testid="cold-process-section">
    <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-20 lg:py-28 grid gap-14 lg:grid-cols-2 items-center">
      <Reveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-signal">Our Product Focus</p>
        <h2 className="mt-4 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tight leading-[0.92]">
          The Cold Process system
        </h2>
        <p className="mt-6 max-w-lg text-base sm:text-lg text-slate-600 leading-relaxed">
          MAXIMUS precured tread rubber is built around the cold process — the tread, the bonding gum and the
          vulcanising solution working as one system to put fresh rubber on a prepared casing, without the cost
          and complexity of hot process retreading.
        </p>
        <ul className="mt-8 space-y-4">
          {["MAX range precured tread rubber", "Cold process bonding gum", "Cold process vulcanising solution"].map((item, i) => (
            <li key={item} className="flex items-center gap-4 border-b border-deep/10 pb-4">
              <span className="font-mono text-[10px] tracking-[0.2em] text-signal">0{i + 1}</span>
              <span className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight">{item}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/products?category=process"
          data-testid="cold-process-explore-button"
          className="group mt-10 inline-flex items-center gap-3 bg-deep px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white hover:bg-signal transition-colors"
        >
          Explore the system
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="relative">
          <div className="clip-corner overflow-hidden">
            <img src={IMAGES.tread1} alt="Precured tread rubber pattern" className="h-80 sm:h-[480px] w-full object-cover" data-testid="cold-process-image" />
          </div>
          <blockquote className="mt-8 border-l-4 border-signal pl-6">
            <p className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight leading-tight">
              "We help our customers achieve high mileage."
            </p>
          </blockquote>
        </div>
      </Reveal>
    </div>
  </section>
);

const CtaBanner = () => (
  <section className="relative overflow-hidden" data-testid="cta-banner">
    <img src={IMAGES.fleet1} alt="Truck fleet on the move" className="absolute inset-0 h-full w-full object-cover" data-testid="cta-banner-image" />
    <div className="absolute inset-0 bg-ink/80" />
    <div className="absolute inset-0 blueprint-grid" />
    <div className="relative mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-24 lg:py-36 text-center">
      <Reveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">Request a Quote</p>
        <h2 className="mt-6 font-display font-black uppercase tracking-tight leading-[0.9] text-[clamp(2.6rem,7vw,6.5rem)] text-white">
          Rebuild today.<br />Go further.
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/contact"
            data-testid="cta-banner-quote-button"
            className="group inline-flex items-center gap-3 bg-signal px-10 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white hover:bg-signalhover transition-colors"
          >
            Request a Quote
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={`tel:+91${CONTACT.phones[0]}`}
            data-testid="cta-banner-call-button"
            className="inline-flex items-center gap-3 border border-white/25 px-10 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white hover:border-amber hover:text-amber transition-colors"
          >
            <Phone size={14} /> +91 {CONTACT.phones[0]}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const USPBand = () => (
  <section className="border-b border-white/10" data-testid="usp-band">
    <div className="mx-auto max-w-[1440px] grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border-x border-white/10">
      {USPS.map((u, i) => (
        <div key={u.key} className="bg-ink px-5 sm:px-6 py-7 flex items-center gap-4" data-testid={`usp-item-${u.key}`}>
          <span className="grid place-items-center h-11 w-11 rounded-full border-2 border-amber text-amber shrink-0">
            {[<ShieldCheck size={18} key="a" />, <IndianRupee size={18} key="b" />, <Gauge size={18} key="c" />, <Leaf size={18} key="d" />][i]}
          </span>
          <p className="font-display text-lg sm:text-xl font-bold uppercase tracking-tight text-white leading-tight">{u.label}</p>
        </div>
      ))}
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee items={["Precured Tread Rubber", "Bonding Gum", "Vulcanising Solution", "Cold Process", "Built to Last", "More Kilometres", "Higher Savings"]} />
    <USPBand />
    <Manifesto />
    <Categories />
    <Showcase />
    <ColdProcess />
    <CtaBanner />
  </>
);

export default Home;
